import React, { useState, useRef, useEffect } from 'react';
import './FileUpload.css';
import { Button } from 'reactstrap';

const FileUpload = ({ onFilesSelected, maxFiles = 5, maxSizeMB = 10, disabled = false, resetKey = 0 }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const isFirstRenderRef = useRef(true);
  const latestOnFilesSelected = useRef(onFilesSelected);

  useEffect(() => {
    latestOnFilesSelected.current = onFilesSelected;
  }, [onFilesSelected]);

  const createPreview = (file) => {
    if (file.type.startsWith('image/')) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve({
            file,
            preview: event.target.result,
            type: 'image'
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    return Promise.resolve({
      file,
      preview: null,
      type: getFileIcon(file.type)
    });
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);

    // Validate file count
    if (files.length + selectedFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file sizes
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const oversizedFiles = files.filter(f => f.size > maxSizeBytes);
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed ${maxSizeMB}MB limit`);
      return;
    }

    try {
      const previewsFromFiles = await Promise.all(files.map(createPreview));
      updateFilesAndPreviews(files, previewsFromFiles);
    } catch (error) {
      console.error('File preview failed', error);
      alert('Something went wrong while reading one or more files.');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const updateFilesAndPreviews = (files, newPreviews) => {
    const updatedFiles = [...selectedFiles, ...files];
    const updatedPreviews = [...previews, ...newPreviews];
    
    setSelectedFiles(updatedFiles);
    setPreviews(updatedPreviews);
    
    if (onFilesSelected) {
      onFilesSelected(updatedFiles);
    }
  };

  const removeFile = (index) => {
    if (index < 0 || index >= selectedFiles.length) return;
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    
    setSelectedFiles(updatedFiles);
    setPreviews(updatedPreviews);
    
    if (onFilesSelected) {
      onFilesSelected(updatedFiles);
    }
  };

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setSelectedFiles([]);
    setPreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    latestOnFilesSelected.current?.([]);
  }, [resetKey]);

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="file-upload-container">
      {/* File Input (Hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* File Previews */}
      {selectedFiles.length > 0 && (
        <div className="file-previews">
          {previews.map((preview, index) => (
            <div key={index} className="file-preview-item">
              {preview.type === 'image' ? (
                <div className="file-preview-image" style={{ backgroundImage: `url(${preview.preview})` }}>
                  <button
                    type="button"
                    className="file-preview-remove"
                    onClick={() => removeFile(index)}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="file-preview-doc">
                  <div className="file-icon">{preview.type}</div>
                  <div className="file-info">
                    <div className="file-name">{preview.file.name}</div>
                    <div className="file-size">{formatFileSize(preview.file.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="file-preview-remove"
                    onClick={() => removeFile(index)}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      <div className="file-upload-trigger-row">
        <Button
            type="button"
            color="light"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            disabled={disabled || selectedFiles.length >= maxFiles}
        >
            <i className="fa fa-paperclip" aria-hidden="true"></i>
            <span>
              {selectedFiles.length > 0 ? `${selectedFiles.length} attachment${selectedFiles.length > 1 ? 's' : ''}` : 'Attachments'}
            </span>
        </Button>
      </div>
    </div>
  );
};

export default FileUpload;

